import classname from "classnames/bind";
import { useInView } from "motion/react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import YoutubeEmbed from "./YoutubeEmbed";
import { IconButton } from "@components";
import { AcfMediaFragment } from "@graphqlTypes";
import ArrowRightIcon from "@/assets/svg/arrow-right.svg";
import { checkYoutubeIframeApi, getYoutubeThumbnail } from "@/core/utils";
import { getReduceMotion } from '@/utils/reduceMotion';
import { toProxiedWpUrl } from '@/utils/wpMediaUrl';

import styles from "./YoutubeEmbed.module.scss";
const cx = classname.bind(styles);

type YoutubeVideoPreviewProps = {
  id: string;
  previewUrl: string;
  thumbnail?: AcfMediaFragment;
  className?: string;
  loop?: boolean;
  playVideoLabel?: string;
  consentSmall?: boolean;
};

export type PreviewVideoRef = {
  getPlayer: () => VideoPlayer | null;
};

const YoutubeVideoPreview = forwardRef<PreviewVideoRef, YoutubeVideoPreviewProps>((
  {
    id,
    previewUrl,
    thumbnail,
    className,
    loop = true,
    playVideoLabel,
    consentSmall
  },
  ref
) => {
    const videoEl = useRef<HTMLVideoElement>(null);
    const [showEmbed, setShowEmbed] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(rootRef);
    const videoAttached = useRef(false);
    const playerRef = useRef<PreviewVideoRef>(null);
    const posterImage = toProxiedWpUrl(thumbnail?.node?.mediaItemUrl) || getYoutubeThumbnail(id);

    useImperativeHandle(
      ref,
      () => {
        return {
          getPlayer: () => playerRef.current?.getPlayer(),
          showEmbed: () => setShowEmbed(true),
        };
      },
      [showEmbed]
    );

    const onClick = () => {
      setShowEmbed(true);
    };

    // handle parent's click
    useEffect(() => {
      if (!rootRef.current) return;
      const parent = rootRef.current.closest('button');
      if (parent) {
        parent.addEventListener('click', onClick);
      }
      return () => {
        if (parent) {
          parent.removeEventListener('click', onClick);
        }
      };
    }, []);

    // Determine if video should autoplay
    const [autoPlay, setAutoPlay] = useState<boolean>(false);
    useEffect(() => {
      setAutoPlay(!getReduceMotion());
    }, []);

    useEffect(() => {
      if (videoEl.current && !videoAttached.current && isInView) {
        videoEl.current.src = previewUrl;
        videoAttached.current = true;
      }
      if (videoEl.current && isInView && autoPlay) {
        videoEl.current.play();
      } else {
        videoEl.current?.pause();
      }
    }, [isInView, showEmbed]);

    // Returning from the embed remounts the <video>, so the src has to be
    // reattached before the effect above can play it again
    const revertToPreview = () => {
      videoAttached.current = false;
      setShowEmbed(false);
    };

    useEffect(() => {
      checkYoutubeIframeApi();
    }, []);

    return (
      <div
        className={cx(["youtube-video-preview", className])}
        onClick={onClick}
        ref={rootRef}
      >
        {showEmbed ? (
          <YoutubeEmbed
            id={id}
            ref={playerRef}
            consentSmall={consentSmall}
            onConsentedElsewhere={revertToPreview}
          />
        ) : (
          <>
            <video
              className={cx("video")}
              ref={videoEl}
              muted
              poster={posterImage}
              loop={loop}
              playsInline
            />
            <span className={cx("overlay")} />
            <IconButton
              className={cx("play-button")}
              icon={ArrowRightIcon}
              size="large"
              ariaLabel={`Play video ${playVideoLabel || ''}`}
              tag="div"
            />
          </>
        )}
      </div>
    );
  }
);

export default YoutubeVideoPreview;
